import type { LicenseStatus } from "@closer/shared";
import { Button, Card, useToast } from "@closer/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * ABONELİK DURUM BANNER'I VE DÖNEM SONU İPTAL AKSİYONU (CLOS-931, CLOS-203 `[177]`
 * `[S01]`).
 *
 * ### ⚠ Bu artık "gizli otomasyon" DEĞİL
 *
 * `SubscriptionOverviewPage`'in eski notu (*"dosyada tek bir useMutation yok"*) CLOS-203
 * henüz yokken yazılmıştı — o an bir düğme koymak, var olmayan bir command'ı ÇAĞIRIYORMUŞ
 * gibi göstermek olurdu. CLOS-203 artık `POST /api/subscription/cancel` ve `/resume`
 * uçlarını gerçekten sağlıyor (`subscription.plan` yetkisi arkasında); bu kart TAM OLARAK
 * kartın *"yetkili command akışlarına yönlendirir"* dediği yönlendirmenin kendisi.
 *
 * ### ⚠ YETKİ GÖSTERİMDE FİLTRELENMİYOR — sunucu tek gerçek kapı
 *
 * `subscription.plan` yetkisi bu ekranda AYRICA sorulmuyor: düğme herkese görünür, sunucu
 * yetkisiz denemeyi 403 ile reddeder ve hata burada okunur biçimde gösterilir. Bu bilinçli
 * bir sınır — ACIK-KARAR: ince taneli "düğmeyi hiç gösterme" davranışı istenirse
 * `subscription.plan` yetkisini oturum bağlamından okuyan ayrı bir uç gerekir.
 */
export function SubscriptionActionsCard({
  tenantId,
  status,
  periodEndsAt,
}: {
  tenantId: string;
  status: LicenseStatus;
  /** Mevcut dönemin (dolayısıyla `cancel_at_period_end` iptalinin) biteceği tarih. */
  periodEndsAt: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const tazele = async () => {
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "subscription-overview") });
  };

  const iptalEt = useMutation({
    mutationFn: async () =>
      (
        await api<{ effectiveAt: string }>("/api/subscription/cancel", {
          method: "POST",
          tenantId,
          body: "{}",
        })
      ).data,
    onSuccess: async () => {
      await tazele();
      toast.success("Abonelik dönem sonunda sona erecek şekilde işaretlendi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İptal talebi gönderilemedi"),
  });

  const vazgec = useMutation({
    mutationFn: async () =>
      (
        await api<{ status: string }>("/api/subscription/resume", {
          method: "POST",
          tenantId,
          body: "{}",
        })
      ).data,
    onSuccess: async () => {
      await tazele();
      toast.success("İptal talebi geri alındı; abonelik aktif kalmaya devam ediyor");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İptal geri alınamadı"),
  });

  const banner = DURUM_BANNERLARI[status];
  const aksiyonGosterilsin =
    status === "active" || status === "trialing" || status === "cancel_at_period_end";

  // Ne bir banner ne bir aksiyon varsa kart HİÇ render edilmiyor — boş bir kart, sorunsuz
  // bir aboneliği bile "dikkat gerektiriyor" gibi gösterirdi.
  if (!banner && !aksiyonGosterilsin) return null;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        {banner ? (
          /**
           * ⚠ Token DÜZELTİLDİ: `border-warning/40 bg-warning/10` opak OLMAYAN saydamlık
           * kısaltmalarıydı (§1 yasağı — "pastel zeminler artık opak, kendi token'ları").
           * `warning-soft` zaten bu kart ailesinin kanonik zemini; `InfoBanner` kit
           * bileşenine geçilmedi çünkü test `data-testid="abonelik-durum-banner"` arıyor
           * ve `InfoBanner` şu an keyfi HTML özniteliği geçirmiyor.
           */
          <div
            data-testid="abonelik-durum-banner"
            className="rounded-md bg-warning-soft p-3.5 text-sm text-fg"
          >
            <p className="font-medium">{banner.baslik}</p>
            <p className="text-fg-muted">{banner.aciklama}</p>
          </div>
        ) : null}

        {status === "cancel_at_period_end" ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-fg-muted">
              Bu abonelik dönem sonunda ({periodEndsAt}) sona erecek. Mevcut dönem hakkınız
              korunuyor; dilerseniz iptalden vazgeçebilirsiniz.
            </p>
            <div>
              <Button
                variant="secondary"
                size="sm"
                loading={vazgec.isPending}
                onClick={() => vazgec.mutate()}
              >
                İptalden vazgeç
              </Button>
            </div>
          </div>
        ) : status === "active" || status === "trialing" ? (
          /*
            ⚠ Açıklama, iptal dalındaki gibi burada da gerekiyor. Sorunsuz bir abonelikte
            bu kartın banner'ı olmuyor; açıklama da yokken ekranda başlıksız bir kutunun
            içinde tek başına "Dönem sonunda iptal et" düğmesi kalıyor ve düğmenin neyi
            ne zaman bitireceği hiçbir yerde yazmıyor.
          */
          <div className="flex flex-col gap-2">
            <p className="text-sm text-fg-muted">
              Aboneliğiniz açık. İptal ederseniz dönem sonunda ({periodEndsAt}) sona erer; mevcut
              dönem hakkınız o güne kadar korunur.
            </p>
            <div>
              <Button
                variant="secondary"
                size="sm"
                loading={iptalEt.isPending}
                onClick={() => iptalEt.mutate()}
              >
                Dönem sonunda iptal et
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Ödeme sorunu/kısıt durumlarının AÇIKLAYICI metni (CLOS-931).
 *
 * ⚠ `active`/`trialing`/`cancel_at_period_end` burada YOK — sorunsuz durumlar banner
 * istemiyor, `cancel_at_period_end`'in kendi mesajı yukarıda ayrı yazılı (etkili tarih
 * taşıdığı için sabit bir sözlük satırına sığmıyor).
 */
const DURUM_BANNERLARI: Partial<Record<LicenseStatus, { baslik: string; aciklama: string }>> = {
  past_due: {
    baslik: "Ödeme bekleniyor",
    aciklama: "Son ödeme denemesi başarısız oldu; sağlayıcı kısa süre içinde tekrar deneyecek.",
  },
  grace: {
    baslik: "Ek süre içinde",
    aciklama:
      "Ödeme henüz doğrulanmadı. Mevcut verilerinize erişiminiz sürüyor; ödeme güncellenmezse hesap Kısıtlı duruma geçer.",
  },
  restricted: {
    baslik: "Kısıtlı",
    aciklama:
      "Mevcut kayıtlarınızı görüntüleyip dışa aktarabilirsiniz; yeni sözleşme, davet veya ek hak gibi işlemler ödeme doğrulanana kadar kapalı.",
  },
  expired: {
    baslik: "Abonelik sona erdi",
    aciklama: "Bu aboneliğin dönemi kapandı. Devam etmek için yeni bir abonelik gerekir.",
  },
};
